import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { formatDurationShort, localToday, shiftWeek, weekDays, weekKey } from '@battleops/shared';
import { useWeekStrip } from '@battleops/client/src/data/useWeekStrip';
import { useTasks } from '@battleops/client/src/context/TaskContext';
import { useProjects } from '@battleops/client/src/context/ProjectContext';
import { useTaskModal } from '../hooks';
import Icon from './Icon';
import s from './WeekStrip.module.css';

// The week on every Home (Lj, Nov 2: "a calendar strip for the week u can click on the day and add tasks … and it puts them
// in our actual schedule", "globalized", "weather forcasting"). Days coloured by how full they are, the forecast, jobs due;
// a click shows the day and adds a task to it (scheduledDate — the same schedule the Week, Plan and calendar read).

const hm = (min) => formatDurationShort(min * 60000);
const dayLabel = (d, o) => new Date(`${d}T12:00:00`).toLocaleDateString(undefined, o);
const FILL = { full: 'Full day', part: 'Part day', light: 'Light' };

export default function WeekStrip() {
  const today = localToday();
  const [key, setKey] = useState(weekKey(today));
  const [pick, setPick] = useState(today);
  const w = useWeekStrip(key === weekKey(today) ? today : weekDays(key)[0]);
  const { addTask, clockedIn } = useTasks();
  const { projects } = useProjects();
  const details = useTaskModal();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [job, setJob] = useState(clockedIn?.projectId || '');
  const sel = w.days.find((d) => d.date === pick) || w.days.find((d) => d.today) || w.days[0];
  const jobs = useMemo(() => projects.filter((p) => (p.status || 'active') === 'active' && !p.deletedAt && !p.archived).sort((a, b) => String(a.name).localeCompare(String(b.name))), [projects]);
  const move = (n) => { const k = shiftWeek(key, n); setKey(k); setPick(k === weekKey(today) ? today : weekDays(k)[0]); };
  const add = () => {
    const t = title.trim(); if (!t) return;
    addTask({ title: t, scheduledDate: sel.date, ...(job ? { projectId: job } : {}) });
    setTitle('');
  };
  return (
    <section className={s.card} aria-label="This week">
      <div className={s.head}>
        <h2>{key === weekKey(today) ? 'This week' : `Week of ${dayLabel(w.from, { month: 'short', day: 'numeric' })}`}</h2>
        <span className={s.range}>{dayLabel(w.from, { month: 'short', day: 'numeric' })} – {dayLabel(w.to, { month: 'short', day: 'numeric' })}</span>
        <span className="spacer" />
        {w.site ? <button type="button" className={s.site} onClick={() => navigate('/forecast')} title="Open the forecast"><Icon name="cloud" size={12} />{w.site}</button> : null}
        <button type="button" className={s.nav} onClick={() => move(-1)} aria-label="Last week"><Icon name="chevron-left" size={16} /></button>
        {key !== weekKey(today) ? <button type="button" className={s.navText} onClick={() => { setKey(weekKey(today)); setPick(today); }}>Today</button> : null}
        <button type="button" className={s.nav} onClick={() => move(1)} aria-label="Next week"><Icon name="chevron-right" size={16} /></button>
        <button type="button" className={s.navText} onClick={() => navigate(`/week/${key}`)}>Week plan</button>
      </div>
      <div className={s.days} role="listbox" aria-label="Days">
        {w.days.map((d) => (
          <button key={d.date} type="button" role="option" aria-selected={d.date === sel.date} className={s.day} data-on={d.date === sel.date || undefined} data-today={d.today || undefined} data-past={d.past || undefined}
            onClick={() => setPick(d.date)} aria-label={`${dayLabel(d.date, { weekday: 'long', month: 'long', day: 'numeric' })}: ${d.open + d.done} tasks${d.weather?.summary ? `, ${d.weather.summary}` : ''}${d.due.length ? `, ${d.due.map((x) => x.name).join(', ')} due` : ''}`}>
            <span className={s.dow}>{dayLabel(d.date, { weekday: 'short' })}</span>
            <b className={`${s.num} num`}>{dayLabel(d.date, { day: 'numeric' })}</b>
            {d.weather ? (
              <span className={s.sky} data-wet={d.weather.wet || undefined}><Icon name={d.weather.icon} size={16} />{d.weather.hi != null ? `${d.weather.hi}°` : d.weather.lo != null ? `${d.weather.lo}°` : ''}</span>
            ) : <span className={s.sky} />}
            <span className={s.fill} data-fill={d.fill || undefined} title={d.fill ? `${FILL[d.fill]}${d.plannedMin ? ` · ${hm(d.plannedMin)} planned` : ''}` : 'Nothing planned'}><i /></span>
            <span className={s.count}>{d.open + d.done ? `${d.done ? `${d.done}/` : ''}${d.open + d.done} task${d.open + d.done === 1 ? '' : 's'}` : '—'}</span>
            {d.due.length ? <span className={s.due} title={`${d.due.map((x) => x.name).join(', ')} due`}><Icon name="star" size={11} /></span> : null}
          </button>
        ))}
      </div>
      <div className={s.legend}><span><i data-fill="full" />Full</span><span><i data-fill="part" />Part</span><span><i data-fill="light" />Light</span><span><Icon name="star" size={11} />Job due</span></div>
      <div className={s.detail}>
        <div className={s.detailHead}>
          <b>{dayLabel(sel.date, { weekday: 'long', month: 'short', day: 'numeric' })}</b>
          {sel.weather ? <span>{[sel.weather.summary, sel.weather.hi != null ? `high ${sel.weather.hi}°` : null, sel.weather.lo != null ? `low ${sel.weather.lo}°` : null, sel.weather.pop ? `${sel.weather.pop}% chance of precipitation` : null].filter(Boolean).join(' · ')}</span> : null}
          {sel.plannedMin ? <span className="num">{hm(sel.plannedMin)} planned</span> : null}
        </div>
        {sel.due.map((x) => <button key={x.id} type="button" className={s.row} onClick={() => navigate(`/projects/${x.id}`)}><Icon name="star" size={13} /><span className={s.ell}>{x.name} due</span></button>)}
        {sel.events.map((e) => <div key={e.id} className={s.row}><Icon name="calendar" size={13} /><span className={s.ell}>{e.title}</span><span className={s.dim}>{e.allDay ? 'All day' : e.start || ''}</span></div>)}
        {sel.late ? <div className={s.row}><Icon name="alert-triangle" size={13} /><span className={s.ell}>{sel.late} late from earlier days</span></div> : null}
        {sel.jobs.map((g) => (
          <div key={g.projectId || 'none'} className={s.group}>
            <h3><i style={{ background: g.color || 'var(--c-text-tertiary)' }} />{g.name}</h3>
            {g.tasks.map((t) => (
              <button key={t.id} type="button" className={s.row} onClick={() => details.open(t.id)}>
                <span className={s.tick} data-done={t.done || undefined}>{t.done ? '✓' : ''}</span>
                <span className={s.ell} data-done={t.done || undefined}>{t.title}</span>
                <span className={s.dim}>{t.estMin ? hm(t.estMin) : ''}</span>
              </button>
            ))}
          </div>
        ))}
        <div className={s.add}>
          <Icon name="plus" size={14} />
          <input value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(); }} placeholder={`Add a task for ${dayLabel(sel.date, { weekday: 'long' })}`} aria-label={`New task for ${sel.date}`} />
          <select value={job} onChange={(e) => setJob(e.target.value)} aria-label="Job">
            <option value="">No job</option>
            {jobs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button type="button" className="btn sm" disabled={!title.trim()} onClick={add}>Add</button>
        </div>
      </div>
    </section>
  );
}

